"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDeepCompareEffectNoCheck = exports.useDeepCompareEffect = exports.useDeepCompareMemoize = void 0;
const react_1 = require("react");
const utils_1 = require("./utils");
function isPrimitive(val) {
    return val == null || /^[sbn]/.test(typeof val);
}
function checkDeps(deps) {
    if (!Array.isArray(deps)) {
        throw new Error("useDeepCompareEffect received a final argument that is not an array!");
    }
    if (deps.length === 0) {
        throw new Error("useDeepCompareEffect should not be used with no dependencies. Use React.useEffect instead.");
    }
    if (deps.every(isPrimitive)) {
        throw new Error("useDeepCompareEffect should not be used with dependencies that are all primitive values. Use React.useEffect instead.");
    }
}
/**
 * Deeply clones a given value. This function handles primitives, arrays, and objects.
 *
 * @template T - The type of the value to be cloned.
 * @param {T} value - The value to be cloned. Can be a primitive, array, or object.
 * @returns {T} - A deep clone of the input value.
 *
 * @example
 * const original = { a: 1, b: { c: 2 } };
 * const clone = deepClone(original);
 * console.log(clone); // { a: 1, b: { c: 2 } }
 * console.log(clone === original); // false
 * console.log(clone.b === original.b); // false
 */
function deepClone(value) {
    // Возвращаем примитивы и null без изменений
    if (value === null || typeof value !== "object") {
        return value;
    }
    // Для массивов рекурсивно клонируем каждый элемент
    if (Array.isArray(value)) {
        return value.map(deepClone);
    }
    // Для объектов создаем пустой клон
    const cloned = {};
    // Рекурсивно клонируем каждое свойство объекта
    Object.entries(value).forEach(([key, val]) => {
        cloned[key] = deepClone(val);
    });
    return cloned;
}
/**
 * @param value the value to be memoized (usually a dependency list)
 * @returns a memoized version of the value as long as it remains deeply equal
 */
const useDeepCompareMemoize = (value) => {
    // Создаем ref для хранения мемоизированного значения
    const ref = (0, react_1.useRef)();
    // Первичная инициализация: клонируем значение в ref
    if (ref.current === undefined) {
        ref.current = deepClone(value);
        return ref.current;
    }
    // Для примитивов делаем простое сравнение без глубокого клонирования
    if (typeof value !== "object" || value === null) {
        if (value !== ref.current) {
            ref.current = value;
        }
        return ref.current;
    }
    // Для объектов выполняем глубокое сравнение
    // Если значения не равны - клонируем новое значение
    if (!(0, utils_1.deepEqual)(value, ref.current)) {
        ref.current = deepClone(value);
    }
    // Возвращаем актуальное значение из ref
    return ref.current;
};
exports.useDeepCompareMemoize = useDeepCompareMemoize;
const useDeepCompareEffect = (callback, dependencies) => {
    /* javascript-obfuscator:disable */
    const nodeEnv = process.env.NODE_ENV;
    /* javascript-obfuscator:enable */
    // Проверка зависимостей только в development
    if (nodeEnv !== "production") {
        checkDeps(dependencies);
    }
    // Early return for primitive dependencies
    if (dependencies?.every(isPrimitive)) {
        return (0, react_1.useEffect)(callback, dependencies);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return (0, react_1.useEffect)(callback, (0, exports.useDeepCompareMemoize)(dependencies));
};
exports.useDeepCompareEffect = useDeepCompareEffect;
const useDeepCompareEffectNoCheck = (callback, dependencies) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return (0, react_1.useEffect)(callback, (0, exports.useDeepCompareMemoize)(dependencies));
};
exports.useDeepCompareEffectNoCheck = useDeepCompareEffectNoCheck;
//# sourceMappingURL=use-deep-compare-effect.js.map